import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChefHat, CheckCircle2, Clock, XCircle, BedDouble, RefreshCw, AlertCircle, Timer, Package } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faNoteSticky } from "@fortawesome/free-solid-svg-icons";
import "./KitchenDisplay.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface OrderItem { name: string; qty: number; price: number }
interface Order {
  id: number; room_number: string; guest_name?: string;
  items: OrderItem[]; status: "pending" | "preparing" | "delivered" | "cancelled";
  total: number; notes?: string; created_at: string;
}

function LiveTimer({ createdAt, status }: { createdAt: string; status: string }) {
  const [secs, setSecs] = useState(() => Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000));
  useEffect(() => {
    const iv = setInterval(() => setSecs(Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [createdAt]);
  const m = Math.floor(secs / 60), s = secs % 60;
  const danger = (status === "pending" && m >= 10) || (status === "preparing" && m >= 25);
  const warn   = (status === "pending" && m >= 5)  || (status === "preparing" && m >= 15);
  return (
    <span className={`kd-timer ${danger ? "danger" : warn ? "warn" : ""}`}>
      <Timer size={14} />{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </span>
  );
}

export default function KitchenDisplay() {
  const navigate = useNavigate();
  const [orders, setOrders]     = useState<Order[]>([]);
  const [loading, setLoading]   = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [filter, setFilter]     = useState<"all" | "pending" | "preparing">("all");
  const prevIds = useRef<Set<number>>(new Set());
  const audioCtxRef = useRef<AudioContext | null>(null);

  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const init = () => {
      if (!audioCtxRef.current)
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      document.removeEventListener("click", init);
    };
    document.addEventListener("click", init);
    return () => document.removeEventListener("click", init);
  }, []);

  function playDing() {
    const ctx = audioCtxRef.current; if (!ctx) return;
    [880, 660].forEach((freq, i) => {
      const osc = ctx.createOscillator(), gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.18);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 1);
      osc.start(ctx.currentTime + i * 0.18);
      osc.stop(ctx.currentTime + i * 0.18 + 1);
    });
  }

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/roomservice/orders`, { headers });
    const fresh: Order[] = (data || []).filter((o: Order) => o.status === "pending" || o.status === "preparing");
    const newOnes = fresh.filter(o => !prevIds.current.has(o.id));
    if (prevIds.current.size > 0 && newOnes.length > 0) playDing();
    prevIds.current = new Set(fresh.map(o => o.id));
    setOrders(fresh);
    setLastRefresh(new Date());
    setLoading(false);
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, []);

  const updateStatus = async (id: number, status: string) => {
    await axios.put(`${API}/api/roomservice/orders/${id}/status`, { status }, { headers });
    load();
  };

  const visible = orders.filter(o => filter === "all" || o.status === filter);
  const pending   = orders.filter(o => o.status === "pending").length;
  const preparing = orders.filter(o => o.status === "preparing").length;

  return (
    <div className="kd-root">
      <div className="kd-page">

        {/* Header */}
        <div className="kd-header">
          <div>
            <h1 className="kd-title">Kitchen Display</h1>
            <p className="kd-subtitle">
              Last updated {lastRefresh.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })} · auto-refreshes every 10s
            </p>
          </div>
          <div className="kd-header-right">
            <div className={`kd-stat-pill ${pending > 0 ? "warn" : ""}`}>
              <Clock size={14} /> <strong>{pending}</strong> Pending
            </div>
            <div className={`kd-stat-pill ${preparing > 0 ? "active" : ""}`}>
              <ChefHat size={14} /> <strong>{preparing}</strong> Preparing
            </div>
            <div className="kd-filter-btns">
              {(["all", "pending", "preparing"] as const).map(f => (
                <button key={f} className={`kd-filter ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>
                  {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <button className="kd-icon-btn" onClick={load} title="Refresh"><RefreshCw size={15} /></button>
            <button className="kd-icon-btn" onClick={() => navigate("/dashboard/roomservice?tab=orders")} title="Back to orders">
              <Package size={15} />
            </button>
          </div>
        </div>

        {/* Grid */}
        {loading && <div className="kd-loading"><span className="kd-spinner" /> Loading orders…</div>}
        {!loading && visible.length === 0 && (
          <div className="kd-empty"><CheckCircle2 size={40} /><p>All clear — no active orders</p></div>
        )}
        {!loading && visible.length > 0 && (
          <div className="kd-grid">
            {visible.map(order => {
              const isPending = order.status === "pending";
              const minsElapsed = Math.floor((Date.now() - new Date(order.created_at).getTime()) / 60000);
              const isUrgent = isPending && minsElapsed >= 10;
              return (
                <div key={order.id} className={`kd-card ${order.status} ${isUrgent ? "urgent" : ""}`}>
                  <div className="kd-card-top">
                    <div className="kd-room">
                      <BedDouble size={16} />
                      <span>Room {order.room_number}</span>
                      {isUrgent && <span className="kd-urgent-tag"><AlertCircle size={11} /> URGENT</span>}
                    </div>
                    <LiveTimer createdAt={order.created_at} status={order.status} />
                  </div>

                  {order.guest_name && <div className="kd-guest">{order.guest_name}</div>}

                  <div className="kd-items">
                    {(order.items || []).map((item, i) => (
                      <div key={i} className="kd-item">
                        <span className="kd-item-qty">{item.qty}×</span>
                        <span className="kd-item-name">{item.name}</span>
                      </div>
                    ))}
                    {order.notes && <div className="kd-notes"><FontAwesomeIcon icon={faNoteSticky} /> {order.notes}</div>}
                  </div>

                  <div className="kd-actions">
                    {isPending && (
                      <button className="kd-btn kd-btn-prepare" onClick={() => updateStatus(order.id, "preparing")}>
                        <ChefHat size={14} /> Start Preparing
                      </button>
                    )}
                    {!isPending && (
                      <button className="kd-btn kd-btn-deliver" onClick={() => updateStatus(order.id, "delivered")}>
                        <CheckCircle2 size={14} /> Mark Delivered
                      </button>
                    )}
                    <button className="kd-btn kd-btn-cancel" onClick={() => updateStatus(order.id, "cancelled")}>
                      <XCircle size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
